import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
    getDashboardV3TradingKpi,
    getDashboardV3Trading,
} from '../services/dashboardV3.service';
import type {
    DashboardV3TradingDataParams,
    DashboardV3TradingKpiData,
    DashboardV3PredictionGridTradeRow,
    DashboardV3PredictionLeverageTradeRow,
    DashboardV3PredictionSpotTradeRow,
    DashboardV3TradingRow,
} from '../services/dashboardV3.types';

const EMPTY_TRADING_KPI: DashboardV3TradingKpiData = {
    tradingUsers: 0,
    tradingUsersDelta: 0,
    firstTradingUsers: 0,
    firstTradingUsersDelta: 0,
    tradingVolume: 0,
    tradingVolumeDelta: 0,
    premiumTradingVolume: null,
    premiumTradingVolumeDelta: null,
    notionalTradingVolume: null,
    notionalTradingVolumeDelta: null,
    openInterest: 0,
    openInterestDelta: 0,
};

interface UseDashboardV3TradingDataOptions {
    // 合约 tab 的明细列表改由 perpetual-trading 接口提供，此时跳过本 hook 的列表请求
    skipList?: boolean;
}

export function useDashboardV3TradingData(
    params: DashboardV3TradingDataParams,
    options: UseDashboardV3TradingDataOptions = {}
) {
    const { skipList = false } = options;
    const notify = useNotify();
    const t = useTranslate();

    const [kpi, setKpi] = useState<DashboardV3TradingKpiData>(EMPTY_TRADING_KPI);
    const [list, setList] = useState<Array<DashboardV3TradingRow | DashboardV3PredictionSpotTradeRow | DashboardV3PredictionLeverageTradeRow | DashboardV3PredictionGridTradeRow>>([]);
    const [total, setTotal] = useState(0);

    const [kpiLoading, setKpiLoading] = useState(false);
    const [tradingLoading, setTradingLoading] = useState(false);

    const kpiParams = useMemo(
        () => ({
            range: params.range,
            startDate: params.range === 'custom' ? params.startDate : undefined,
            endDate: params.range === 'custom' ? params.endDate : undefined,
            productType: params.productType,
        }),
        [params.range, params.startDate, params.endDate, params.productType]
    );

    // 交易明细列表默认与右上角时间范围无关。预测现货/杠杆/网格有列表专属 UTC+0 时间筛选，
    // 使用 listStartDate/listEndDate，避免跟 KPI 的 range/startDate/endDate 混用。
    const tradingParams = useMemo(
        () => ({
            productType: params.productType,

            startDate:
                params.productType === 'prediction_spot' ||
                params.productType === 'prediction_leverage' ||
                params.productType === 'prediction_grid'
                    ? params.listStartDate
                    : undefined,
            endDate:
                params.productType === 'prediction_spot' ||
                params.productType === 'prediction_leverage' ||
                params.productType === 'prediction_grid'
                    ? params.listEndDate
                    : undefined,

            page: params.page,
            size: params.size,

            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            userId: params.userId,
            agencyUid: params.agencyUid,
            marketId: params.marketId,
            status: params.status,
            gameId: params.gameId,
        }),
        [
            params.productType,
            params.listStartDate,
            params.listEndDate,
            params.page,
            params.size,
            params.sortBy,
            params.sortOrder,
            params.userId,
            params.agencyUid,
            params.marketId,
            params.status,
            params.gameId,
        ]
    );

    /**
     * 交易页 KPI：受右上角时间范围与产品 tab 影响。
     */
    useEffect(() => {
        let alive = true;

        setKpiLoading(true);

        getDashboardV3TradingKpi(kpiParams)
            .then((resp) => {
                if (!alive) return;
                setKpi(resp?.data ?? EMPTY_TRADING_KPI);
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(
                    e?.message ||
                    t('module.dashboardV3.trading.messages.kpiLoadFailed', {
                        _: 'Failed to load trading KPI data',
                    }),
                    { type: 'warning' }
                );
                setKpi(EMPTY_TRADING_KPI);
            })
            .finally(() => {
                if (!alive) return;
                setKpiLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [kpiParams, notify, t]);

    /**
     * 交易明细列表：受时间范围、产品 tab、分页影响。
     */
    useEffect(() => {
        if (skipList) {
            setList([]);
            setTotal(0);
            setTradingLoading(false);
            return;
        }

        let alive = true;

        setTradingLoading(true);

        getDashboardV3Trading(tradingParams)
            .then((resp) => {
                if (!alive) return;
                setList(resp?.data?.list ?? []);
                setTotal(Number(resp?.total ?? 0));
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(
                    e?.message ||
                    t('module.dashboardV3.trading.messages.loadFailed', {
                        _: 'Failed to load trading data',
                    }),
                    { type: 'warning' }
                );
                setList([]);
                setTotal(0);
            })
            .finally(() => {
                if (!alive) return;
                setTradingLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [tradingParams, skipList, notify, t]);

    return {
        kpi,
        list,
        total,

        loading: kpiLoading || tradingLoading,
        kpiLoading,
        tradingLoading,
    };
}
